import * as React from 'react'
import { SectionList, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import TextInput from '@components/textInput/TextInput'
import Loader from '@components/loader/Loader'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'

import api from '@api/api'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useDebounce from '@utils/useDebounce'
import useNavigation from '@layout/useNavigation'

import sanitize from '@utils/Sanitizer'
import Logger from '@utils/logger'

const ITEM_WIDTH = 370
const DRAWER_WIDTH = 300

interface ManifProps {
  manifestation: ManifestationResponse
  stats?: ControleQualiteStatistiqueResponse
}

const ManifCard = ({ manifestation, stats }: ManifProps) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  return (
    <Card
      activeOpacity={0.8}
      onPress={() => navigation.push(`/controles/details/infos?manifestationId=${manifestation.id}`)}>
      <Title>{manifestation.libelle}</Title>

      <ScoreContainer>
        <ScoreInfo>{i18n.t('screens.controles.averageScore')}</ScoreInfo>
        <Score>
          {!!stats && !!stats.tauxConformiteMoyen ? `${Math.round(stats.tauxConformiteMoyen)}%` : i18n.t('errors.NC')}
        </Score>
      </ScoreContainer>
    </Card>
  )
}

const Manif = () => {
  const i18n = useI18n()
  const [{ width }, bp] = useBreakpoint()

  const columns = bp === 'phone' ? 1 : Math.floor((width - DRAWER_WIDTH) / ITEM_WIDTH)

  const [search, debounced, setSearch] = useDebounce('')
  const [loading, setLoading] = React.useState(true)
  const [stats, setStats] = React.useState<Record<string, ControleQualiteStatistiqueResponse>>({})

  const site = useReducer(SiteStore.store, (s) => s.site)

  const { incoming, inProgress, toBeClosed } = useReducer(SiteStore.store, (s) => s.manifestations)

  const manifestations = React.useMemo(
    () => [
      {
        title: i18n.t('screens.manifestations.incoming'),
        data: incoming.filter((m) => sanitize(m.libelle).includes(sanitize(debounced))),
      },
      {
        title: i18n.t('screens.manifestations.inProgress'),
        data: inProgress.filter((m) => sanitize(m.libelle).includes(sanitize(debounced))),
      },
      {
        title: i18n.t('screens.manifestations.toBeClosed'),
        data: toBeClosed.filter((m) => sanitize(m.libelle).includes(sanitize(debounced))),
      },
    ],
    [incoming, inProgress, toBeClosed, debounced]
  )

  const sections = React.useMemo(
    () =>
      manifestations
        .map((s) => {
          const items = s.data.filter((m) => !!stats[m.id])
          const groups = Array.from(new Array(Math.ceil(items.length / columns))).map((_, idx) =>
            items.slice(idx * columns, (idx + 1) * columns)
          )

          return { title: s.title, data: groups }
        })
        .filter((s) => s.data.length > 0),
    [manifestations, stats]
  )

  React.useEffect(() => {
    if (site) {
      api.controle.filtres
        .manifestation(site.id)
        .then((assos) =>
          Promise.all(assos.map((m) => api.controle.stats({ manifestationId: m.id, siteId: site.id }))).then((res) => {
            const manifestationsStats = res.reduce(
              (acc, cur, idx) => ({ ...acc, [assos[idx].id]: cur }),
              {} as Record<string, ControleQualiteStatistiqueResponse>
            )

            setStats(manifestationsStats)
          })
        )
        .catch(Logger.error)
        .finally(() => setLoading(false))
    }
  }, [site])

  const renderItem = (item: ManifestationResponse) => {
    const stat = stats[item.id]

    return <ManifCard key={item.id} manifestation={item} stats={stat} />
  }

  const renderSection = ({ item }: { item: ManifestationResponse[] }) => {
    if (columns === 1) {
      return <>{item.map(renderItem)}</>
    }

    return <Line>{item.map(renderItem)}</Line>
  }

  const renderSectionHeader = ({ section }: { section: (typeof sections)[number] }) => {
    return <SectionHeader>{section.title}</SectionHeader>
  }

  return (
    <MainContainer>
      <SearchContainer>
        <TextInput
          value={search}
          onChange={setSearch}
          placeholder={i18n.t('screens.controles.form.manif.search')}
          left={{ name: 'search' }}
          searchStyle="onLight"
        />
      </SearchContainer>

      {loading ? (
        <Loading>
          <Loader />
        </Loading>
      ) : sections.length === 0 ? (
        <NoContent>{i18n.t('screens.controles.form.manif.empty')}</NoContent>
      ) : (
        <SectionList
          key={columns}
          sections={sections}
          keyExtractor={(item, idx) => `section_${idx}`}
          renderItem={renderSection}
          renderSectionHeader={renderSectionHeader}
          contentContainerStyle={{ paddingHorizontal: bp === 'phone' ? 16 : 24 }}
          extraData={stats}
          stickySectionHeadersEnabled={false}
        />
      )}
    </MainContainer>
  )
}

export default Manif

const MainContainer = styled(View)`
  flex: 1;
`

const Loading = styled(View)`
  flex: 1;
  justify-content: center;
  align-items: center;
`

const SearchContainer = styled(View)`
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
`

const Line = styled(View)`
  flex-direction: row;
`

const SectionHeader = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin: 8px 0;
`

const Card = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  border-radius: 8px;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
  ${(props) => props.theme.breakpoint !== 'phone' && `width: ${ITEM_WIDTH}px;`}
  margin: 8px 0;
  ${(props) => props.theme.breakpoint !== 'phone' && 'margin-right: 16px'};
  background-color: ${(props) => props.theme.colors.background};
  ${(props) => props.theme.constants.shadow};
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  flex: 1;
`

const ScoreContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  padding: 8px 5px;
  margin-left: 16px;
  align-items: center;
`

const ScoreInfo = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  color: ${(props) => props.theme.colors.secondaryText};
`

const Score = styled(Text)`
  ${(props) => props.theme.fonts.Callout};
`

const NoContent = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  text-align: center;
  margin: 24px 0;
`
